import type { OperatorSlug } from '@ev/shared'

/**
 * What the map publishes after filtering, and the only channel by which the
 * filter panel and the search box learn about it.
 *
 * The map already walks the national feature set on every filter change to
 * decide what to draw, so it is the one place that knows how many sites survive
 * the predicate and which they are. It publishes that answer here; nothing else
 * re-runs the predicate to show a count.
 *
 * Contract for the publisher:
 *
 * - `matching` counts sites passing every active facet *and* the text query -
 *   the number the panel shows as "N locations found".
 * - `byOperator` counts sites per operator with the operator facet excluded;
 *   see `tallyStations`, where that exclusion is what keeps the multi-select
 *   from dead-ending.
 * - `matchingIds` are those sites' ids, and double as the "has the map published
 *   yet?" flag: `null` means it has not, so the panel renders no counts rather
 *   than a screenful of zeroes while the artifact is still arriving.
 * - `visible` is the subset inside the current viewport, and changes on every
 *   pan. That is why consumers select the field they need rather than the whole
 *   object.
 */

/** The lifecycle of an artifact-backed view, shared by every loader in the app. */
export type LoadPhase = 'idle' | 'loading' | 'ready' | 'error'

export interface MapResults {
  readonly status: LoadPhase
  /** Sites in the loaded artifact, before filtering. */
  readonly total: number
  /** Sites passing the current filters, anywhere in the country. */
  readonly matching: number
  /** Matching sites inside the current viewport. */
  readonly visible: number
  readonly byOperator: Readonly<Partial<Record<OperatorSlug, number>>>
  /** The same, keyed by folded name, for the networks inside the `other` bucket. */
  readonly byOperatorName: Readonly<Record<string, number>>
  readonly matchingIds: readonly string[] | null
}

export const EMPTY_RESULTS: MapResults = {
  status: 'idle',
  total: 0,
  matching: 0,
  visible: 0,
  byOperator: {},
  byOperatorName: {},
  matchingIds: null,
}

/** False until the map publishes for the first time. */
export const hasPublishedResults = (results: MapResults): boolean => results.matchingIds !== null

export const operatorMatchCount = (results: MapResults, slug: OperatorSlug): number =>
  results.byOperator[slug] ?? 0

/** The same, for an untracked network held as a folded name. */
export const operatorNameMatchCount = (results: MapResults, key: string): number =>
  results.byOperatorName[key] ?? 0

/**
 * Field-wise equality.
 *
 * The map republishes on every camera move; identical counts must not wake the
 * filter panel. The two derived collections are compared by identity because the
 * map memoises them - a new array means a genuinely new filter pass, and a deep
 * compare of 9 000 ids on every `moveend` would cost more than the render it
 * saves.
 */
export const sameResults = (a: MapResults, b: MapResults): boolean =>
  a.status === b.status &&
  a.total === b.total &&
  a.matching === b.matching &&
  a.visible === b.visible &&
  a.byOperator === b.byOperator &&
  a.byOperatorName === b.byOperatorName &&
  a.matchingIds === b.matchingIds
